import { observable, computed, makeObservable } from 'mobx';
import {DomainObject} from 'shared/store';
import {absoluteTimestampDate, dateFormatter, fullName} from 'shared/tools';
import {types, auth} from 'main/tools';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import {browserTimeZoneAbbreviation} from 'shared/tools/timeZones';
import {FunnelStage} from './FunnelStage';
import User from 'main/stores/users/User';
import {AttachedDocument} from 'main/stores/documents';
import {InterviewGuide, InterviewGuideAnswer} from 'main/stores/interview_guides';
import InterviewInvite from './InterviewInvite';
import striptags from 'striptags';
import _ from 'lodash';

function isEmail(obj) {
  return obj._type === types.RECRUITING.TIMELINE.EMAIL;
}

function isFeedback(obj) {
  return obj._type === types.RECRUITING.TIMELINE.FEEDBACK;
}

function isStageChange(obj) {
  return obj._type === types.RECRUITING.TIMELINE.STAGE_CHANGE;
}

function isQualificationStatusChange(obj) {
  return obj._type === types.RECRUITING.TIMELINE.QUALIFICATION_STATUS_CHANGE;
}

function isPositionChange(obj) {
  return obj._type === types.RECRUITING.TIMELINE.POSITION_CHANGE;
}

function isUpload(obj) {
  return obj._type === types.RECRUITING.TIMELINE.UPLOAD;
}

function isOffer(obj) {
  return obj._type === types.RECRUITING.TIMELINE.OFFER;
}

function isRating(obj) {
  return obj._type === types.RECRUITING.TIMELINE.RATING;
}

function isInterviewGuideResponse(obj) {
  return obj._type === types.RECRUITING.TIMELINE.INTERVIEW_GUIDE_RESPONSE;
}

function isInterviewInviteAction(obj) {
  return obj._type === types.RECRUITING.TIMELINE.INTERVIEW_INVITE_ACTION;
}

class TimelineEntry extends DomainObject {
  id;
  user;
  text;
  createdAt;
  funnelStage;
  _type;

  _relations = {
    funnelStage: FunnelStage,
    user: User,
    attachments: [AttachedDocument],
    interviewGuide: InterviewGuide,
    answers: [InterviewGuideAnswer],
    interviewInvite: InterviewInvite
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      user: observable,
      text: observable,
      createdAt: observable,
      funnelStage: observable,
      _type: observable,
      date: computed,
      name: computed,
      isFeedback: computed,
      isStageChange: computed,
      isQualificationStatusChange: computed,
      isPositionChange: computed,
      isEmail: computed,
      isUpload: computed,
      isOffer: computed,
      isRating: computed,
      isInterviewGuideResponse: computed,
      isInterviewInviteAction: computed
    });

    this.merge(other);
  }

  get date() {
    return dateFormatter(this.createdAt);
  }

  get name() {
    return fullName(this.user);
  }

  get isFeedback() {
    return isFeedback(this);
  }

  get isStageChange() {
    return isStageChange(this);
  }

  get isQualificationStatusChange() {
    return isQualificationStatusChange(this);
  }

  get isPositionChange() {
    return isPositionChange(this);
  }

  get isEmail() {
    return isEmail(this);
  }

  get isUpload() {
    return isUpload(this);
  }

  get isOffer() {
    return isOffer(this);
  }

  get isRating() {
    return isRating(this);
  }

  get isInterviewGuideResponse() {
    return isInterviewGuideResponse(this);
  }

  get isInterviewInviteAction() {
    return isInterviewInviteAction(this);
  }
}

class QualificationStatusChange extends TimelineEntry {
  qualificationStatus;

  get isDisqualified() {
    return this.qualificationStatus === 'disqualified';
  }

  constructor(other) {
    super();

    makeObservable(this, {
      qualificationStatus: observable,
      isDisqualified: computed
    });

    this._type = types.RECRUITING.TIMELINE.QUALIFICATION_STATUS_CHANGE;

    this.merge(other);
  }
}

class StageChange extends TimelineEntry {
  nextFunnelStageName;
  direction;

  get isForward() {
    return this.direction === 'forwards';
  }

  constructor(other) {
    super();

    makeObservable(this, {
      nextFunnelStageName: observable,
      direction: observable,
      isForward: computed
    });

    this._type = types.RECRUITING.TIMELINE.STAGE_CHANGE;

    this.merge(other);
  }
}

class TimelineRating extends TimelineEntry {
  score;

  constructor(other) {
    super();

    makeObservable(this, {
      score: observable
    });

    this._type = types.RECRUITING.TIMELINE.RATING;

    this.merge(other);
  }
}

class PositionChange extends TimelineEntry {
  oldPositionName;
  newPositionName;

  constructor(other) {
    super();

    makeObservable(this, {
      oldPositionName: observable,
      newPositionName: observable
    });

    this._type = types.RECRUITING.TIMELINE.POSITION_CHANGE;

    this.merge(other);
  }
}

class TimelineFeedback extends TimelineEntry {
  sentiment;
  lexicalState;

  constructor(other) {
    super();

    makeObservable(this, {
      sentiment: observable,
      lexicalState: observable,
      isPositiveSentiment: computed,
      isNegativeSentiment: computed
    });

    this._type = types.RECRUITING.TIMELINE.FEEDBACK;

    this.merge(other);
  }

  get isPositiveSentiment() {
    return this.sentiment === 'positive';
  }

  get isNegativeSentiment() {
    return this.sentiment === 'negative';
  }
}

class TimelineEmail extends TimelineEntry {
  subject;
  body;
  lexicalState;
  external;
  status;
  externalUserInitials;
  attachments = [];

  constructor(other) {
    super();

    makeObservable(this, {
      subject: observable,
      body: observable,
      lexicalState: observable,
      external: observable,
      status: observable,
      externalUserInitials: observable,
      attachments: observable,
      inbound: computed,
      outbound: computed,
      statusColour: computed,
      strippedBody: computed
    });

    this._type = types.RECRUITING.TIMELINE.EMAIL;

    this.merge(other);
  }

  get inbound() {
    return this.direction === 'inbound';
  }

  get outbound() {
    return this.direction === 'outbound';
  }

  get statusColour() {
    if (this.status === 'unopened') {
      return 'jumbo';
    }
    else if (this.status === 'opened') {
      return 'meadow';
    }
    else if (this.status === 'delivery_failed') {
      return 'scarlet';
    }

    return '';
  }

  get strippedBody() {
    return striptags(this.body);
  }
}

class TimelineUpload extends TimelineEntry {
  attachments = [];

  constructor(other) {
    super();

    makeObservable(this, {
      attachments: observable
    });

    this._type = types.RECRUITING.TIMELINE.UPLOAD;

    this.merge(other);
  }
}

class OfferAction extends TimelineEntry {
  offerId;
  action;

  constructor(other) {
    super();

    makeObservable(this, {
      offerId: observable,
      action: observable
    });

    this._type = types.RECRUITING.TIMELINE.OFFER;

    this.merge(other);
  }
}

class InterviewGuideResponse extends TimelineEntry {
  interviewGuide;
  answers = [];

  constructor(other) {
    super();

    makeObservable(this, {
      interviewGuide: observable,
      answers: observable,
      sortedAnswers: computed,
      hasAnswerContent: computed
    });

    this._type = types.RECRUITING.TIMELINE.INTERVIEW_GUIDE_RESPONSE;

    this.merge(other);
  }

  get sortedAnswers() {
    return _.orderBy(this.answers, ['question.order'], ['asc']);
  }

  get hasAnswerContent() {
    let hasContent = false;
    _.forEach(this.answers, (answer) => {
      if (answer.content) hasContent = true;
    });

    return hasContent;
  }
}

class InterviewInviteAction extends TimelineEntry {
  action;
  interviewInvite;
  relatedAttributes = {};
  subject;
  body;
  status;

  constructor(other) {
    super();

    makeObservable(this, {
      action: observable,
      interviewInvite: observable,
      relatedAttributes: observable,
      subject: observable,
      body: observable,
      status: observable,
      eventStartsAtView: computed,
      previousEventStartsAtView: computed,
      statusColour: computed
    });

    this._type = types.RECRUITING.TIMELINE.INTERVIEW_INVITE_ACTION;

    this.merge(other);
  }

  get eventStartsAtView() {
    const {eventStartsAt} = this.relatedAttributes;

    if (!eventStartsAt) return null;

    return `${absoluteTimestampDate(eventStartsAt)} (${browserTimeZoneAbbreviation(new Date(eventStartsAt))})`;
  }

  get previousEventStartsAtView() {
    const {previousEventStartsAt} = this.relatedAttributes;

    if (!previousEventStartsAt) return null;

    return `${absoluteTimestampDate(previousEventStartsAt)} (${browserTimeZoneAbbreviation(new Date(previousEventStartsAt))})`;
  }

  get statusColour() {
    if (this.status === 'unopened') {
      return 'jumbo';
    }
    else if (this.status === 'opened') {
      return 'meadow';
    }
    else if (this.status === 'delivery_failed') {
      return 'scarlet';
    }

    return '';
  }
}

TimelineEntry.fromJS = (json) => {
  if (isFeedback(json)) {
    return new TimelineFeedback(json);
  } else if (isStageChange(json)) {
    return new StageChange(json);
  } else if (isQualificationStatusChange(json)) {
    return new QualificationStatusChange(json);
  } else if (isEmail(json)) {
    return new TimelineEmail(json);
  } else if (isPositionChange(json)) {
    return new PositionChange(json);
  } else if (isUpload(json)) {
    return new TimelineUpload(json);
  } else if (isOffer(json)) {
    return new OfferAction(json);
  } else if (isRating(json)) {
    return new TimelineRating(json);
  } else if (isInterviewGuideResponse(json)) {
    return new InterviewGuideResponse(json);
  } else if (isInterviewInviteAction(json)) {
    return new InterviewInviteAction(json);
  }

  throw new Error(`TimelineEntry type ${json._type} is not supported.`);
};

TimelineEntry.createFeedback = (json) => {
  const feedback = new TimelineFeedback({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format()
  });
  feedback.merge(json);
  return feedback;
};

TimelineEntry.createRating = (json) => {
  const rating = new TimelineRating({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format()
  });
  rating.merge(json);
  return rating;
};

TimelineEntry.createEmail = (json) => {
  const email = new TimelineEmail({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format()
  });
  email.merge(json);
  return email;
};

TimelineEntry.createPositionChange = (json) => {
  const change = new PositionChange({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format(),
  });
  change.merge(json);
  return change;
};

TimelineEntry.createUpload = (json) => {
  const change = new TimelineUpload({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format()
  });
  change.merge(json);
  return change;
};

TimelineEntry.createInterviewGuideResponse = (json) => {
  const response = new InterviewGuideResponse({
    id: uuidv4(),
    user: auth.user,
    createdAt: moment().format()
  });
  response.merge(json);
  return response;
};

export default TimelineEntry;
